import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import {
  checkPermission,
  getModuleActions,
} from "../../../Configurations/PermissionConfigrations";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import AdmissionFeePortalDialog from "./AdmissionFeePortalDialog";
import {
  emsBatchListRowMatchesLoggedInTrainer,
  emsRowMatchesLoggedInTrainer,
} from "../../../Utils/emsTrainerUserMatch";
import { buildMetadataFiltersFromSearchParams } from "../../../Utils/metadataListFilters";

const METADATA_DEFAULT_URL_PARAMS = {
  ems_leads: {
    page: "1",
    pageSize: "25",
    sortBy: "lead_datetime",
    sortOrder: "desc",
  },
  ems_lead_activities: {
    page: "1",
    pageSize: "25",
    sortBy: "activity_datetime",
    sortOrder: "desc",
  },
  ems_lead_contact_timings: {
    page: "1",
    pageSize: "25",
    sortBy: "organization_ems_lead_contact_timing_id",
    sortOrder: "desc",
  },
  ems_demo_sessions: {
    page: "1",
    pageSize: "25",
    sortBy: "demo_date",
    sortOrder: "desc",
  },
  ems_demo_participants: {
    page: "1",
    pageSize: "25",
    sortBy: "organization_ems_demo_participant_id",
    sortOrder: "desc",
  },
  ems_students: {
    page: "1",
    pageSize: "25",
    sortBy: "created_at",
    sortOrder: "desc",
  },
  ems_admissions: {
    page: "1",
    pageSize: "25",
    sortBy: "admission_date",
    sortOrder: "desc",
  },
  ems_fee_installments: {
    page: "1",
    pageSize: "25",
    sortBy: "due_date",
    sortOrder: "asc",
  },
  ems_student_fees: {
    page: "1",
    pageSize: "25",
    sortBy: "payment_date",
    sortOrder: "desc",
  },
  ems_batches: {
    page: "1",
    pageSize: "25",
    sortBy: "start_date",
    sortOrder: "desc",
  },
  ems_batch_students: {
    page: "1",
    pageSize: "25",
    sortBy: "enrollment_date",
    sortOrder: "desc",
    batch_status: "Pursuing",
  },
  ems_batch_classes: {
    page: "1",
    pageSize: "25",
    sortBy: "start_time",
    sortOrder: "asc",
  },
  ems_batch_class_records: {
    page: "1",
    pageSize: "25",
    sortBy: "start_time",
    sortOrder: "desc",
  },
  ems_class_attendance: {
    page: "1",
    pageSize: "25",
    sortBy: "attendance_date",
    sortOrder: "desc",
  },
  ems_assessments: {
    page: "1",
    pageSize: "25",
    sortBy: "assessment_date",
    sortOrder: "desc",
  },
  ems_assessment_results: {
    page: "1",
    pageSize: "25",
    sortBy: "organization_ems_assessment_result_id",
    sortOrder: "desc",
  },
  ems_training_programs: {
    page: "1",
    pageSize: "25",
    sortBy: "training_program_name",
    sortOrder: "asc",
  },
  ems_training_program_categories: {
    page: "1",
    pageSize: "25",
    sortBy: "training_program_category_name",
    sortOrder: "asc",
  },
  ems_certificates: {
    page: "1",
    pageSize: "25",
    sortBy: "issue_date",
    sortOrder: "desc",
  },
  ems_recruitment_agencies: {
    page: "1",
    pageSize: "25",
    sortBy: "agency_name",
    sortOrder: "asc",
  },
  ems_companies: {
    page: "1",
    pageSize: "25",
    sortBy: "company_name",
    sortOrder: "asc",
  },
  ems_placement_referrals: {
    page: "1",
    pageSize: "25",
    sortBy: "referral_date",
    sortOrder: "desc",
  },
  ems_student_review_platforms: {
    page: "1",
    pageSize: "25",
    sortBy: "student_review_platform_name",
    sortOrder: "asc",
  },
  ems_student_reviews: {
    page: "1",
    pageSize: "25",
    sortBy: "review_requested_date",
    sortOrder: "desc",
  },
  ems_lead_stages: {
    page: "1",
    pageSize: "25",
    sortBy: "lead_stage_sequence_number",
    sortOrder: "asc",
  },
  ems_lead_sources: {
    page: "1",
    pageSize: "25",
    sortBy: "lead_source_name",
    sortOrder: "asc",
  },
  ems_training_spaces: {
    page: "1",
    pageSize: "25",
    sortBy: "space_name",
    sortOrder: "asc",
  },
};

const FALLBACK_METADATA_DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "25",
};

function resolveMetadataDefaultUrlParams(filterModule, tableName) {
  const key = filterModule || tableName;
  return (
    METADATA_DEFAULT_URL_PARAMS[key] ||
    METADATA_DEFAULT_URL_PARAMS[tableName] ||
    FALLBACK_METADATA_DEFAULT_URL_PARAMS
  );
}

function resolveMetadataDefaultSortField(filterModule, tableName) {
  const params = resolveMetadataDefaultUrlParams(filterModule, tableName);
  return params.sortBy || "id";
}

const DEFAULT_METADATA_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

/** Parses Lead list cell datetime across list/API formats. */
function parseLeadListDateTime(str) {
  if (!str || str === "-") return null;
  const value = String(str).trim();

  // Display format from FormConfigurations.formatDateTime: DD-MM-YYYY h:mm AM/PM
  let m = value.match(
    /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
  );
  if (m) {
    const [, d, mo, y, h12, mi, ampmRaw] = m;
    const ampm = String(ampmRaw).toUpperCase();
    let h = Number(h12) % 12;
    if (ampm === "PM") h += 12;
    return new Date(Number(y), Number(mo) - 1, Number(d), h, Number(mi), 0);
  }

  // Legacy/alternate display format: DD-MM-YYYY HH:mm:ss
  m = value.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (m) {
    const [, d, mo, y, h, mi, s] = m;
    return new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s)
    );
  }

  // API/server format often arrives as YYYY-MM-DD HH:mm:ss
  m = value.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (m) {
    const [, y, mo, d, h, mi, s] = m;
    return new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s)
    );
  }

  // Last fallback for ISO-ish strings.
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Client-side filter for /leads when opened with ?period=today|month (matches displayed lead_datetime). */
function filterLeadsRowsByPeriod(rows, period) {
  if (!rows?.length || !period) return rows;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart);
  todayEnd.setHours(23, 59, 59, 999);

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );

  return rows.filter((row) => {
    const dt = parseLeadListDateTime(row.lead_datetime);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    if (period === "today") {
      return dt >= todayStart && dt <= todayEnd;
    }
    if (period === "month") {
      return dt >= monthStart && dt <= monthEnd;
    }
    return true;
  });
}

/** Parses demo session list cell `DD-MM-YYYY` (FormatDateV1 in FormConfigurations). */
function parseDemoDateCell(str) {
  if (!str || str === "-") return null;
  const m = String(str).trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/** Client-side filter for /demo-sessions when opened with ?period=today. */
function filterDemoSessionsRowsToday(rows) {
  if (!rows?.length) return rows;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const day = now.getDate();
  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.demo_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return (
      dt.getFullYear() === y &&
      dt.getMonth() === m &&
      dt.getDate() === day
    );
  });
}

/** Client-side filter for /demo-sessions when opened with ?period=month (and optional ?month=YYYY-MM). */
function filterDemoSessionsRowsByMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    // Fallback to "current month"
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.demo_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/** Client-side filter for /leads when opened with ?period=month (and optional ?month=YYYY-MM). */
function filterLeadsRowsByMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    // Fallback to "current month"
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseLeadListDateTime(row.lead_datetime);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/**
 * Laravel may return a plain array in `response.data.data`, or a paginator
 * `{ data: [...], current_page, total, ... }`. Unwrap to an array for mapping.
 */
function normalizeListPayload(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [];
}

function parseLeadDateFilter(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;

  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));

  m = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Parses admission_date: API usually `YYYY-MM-DD`; also accepts `DD-MM-YYYY`. */
function parseAdmissionDateCell(str) {
  if (!str || str === "-") return null;
  const s = String(str).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, mo, d] = iso;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) {
    const [, d, mo, y] = dmy;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  return null;
}

/** Admissions with admission_date = today (local calendar). */
function filterAdmissionsRowsAdmissionToday(rows) {
  if (!rows?.length) return rows;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const day = now.getDate();
  return rows.filter((row) => {
    const dt = parseAdmissionDateCell(row.admission_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return (
      dt.getFullYear() === y &&
      dt.getMonth() === m &&
      dt.getDate() === day
    );
  });
}

/** Admissions with admission_date in a month (optional ?month=YYYY-MM, else current month). */
function filterAdmissionsRowsByMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseAdmissionDateCell(row.admission_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/** Fee installments: due_date is shown as `DD-MM-YYYY` (FormatDateV1). */
function filterFeeInstallmentsByDuePeriod(rows, period) {
  if (!rows?.length || !period) return rows;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart);
  todayEnd.setHours(23, 59, 59, 999);

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );

  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.due_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    if (period === "today") {
      return dt >= todayStart && dt <= todayEnd;
    }
    if (period === "month") {
      return dt >= monthStart && dt <= monthEnd;
    }
    return true;
  });
}

/** Fee installments due in selected month (`?month=YYYY-MM`), else current month. */
function filterFeeInstallmentsByDueMonth(rows, monthYYYYMM) {
  if (!rows?.length) return rows;
  let start;
  let end;

  if (monthYYYYMM && typeof monthYYYYMM === "string" && monthYYYYMM.includes("-")) {
    const match = monthYYYYMM.trim().match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const [, y, mo] = match;
      start = new Date(Number(y), Number(mo) - 1, 1, 0, 0, 0, 0);
      end = new Date(Number(y), Number(mo), 0, 23, 59, 59, 999);
    }
  }

  if (!start || !end) {
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  return rows.filter((row) => {
    const dt = parseDemoDateCell(row.due_date);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= end;
  });
}

/** Parses fee installment due date from raw ISO or list cell `DD-MM-YYYY`. */
function parseFeeInstallmentDueDate(row) {
  const raw =
    row?.payment_date_prefill ??
    row?.due_date_raw ??
    row?.due_date ??
    null;
  if (!raw || raw === "-") return null;
  const iso = String(raw).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, mo, d] = iso;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  return parseDemoDateCell(row.due_date);
}

function filterFeeInstallmentsOverdue(rows) {
  if (!rows?.length) return rows;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return rows.filter((row) => {
    const dt = parseFeeInstallmentDueDate(row);
    if (!dt || Number.isNaN(dt.getTime())) return false;
    if (dt >= todayStart) return false;
    const st = String(row.status ?? "")
      .trim()
      .toLowerCase();
    if (st === "paid" || st === "cancelled") return false;
    return true;
  });
}

function resolveOrganizationId(userData) {
  return (
    userData?.organization?.organization_id ??
    userData?.organization_id ??
    userData?.user_active_organization?.organization_id ??
    null
  );
}

function resolveOrganizationUserId(userData) {
  return (
    userData?.organization_user_id ??
    userData?.organization_user?.organization_user_id ??
    userData?.user_active_organization?.organization_user_id ??
    null
  );
}

function CommonList({
  title,
  apiEndpoint,
  filteredApiEndpoint = "",
  filterModule = "",
  useMetadataFilters = false,
  addRoute,
  editRouteBase,
  mapResponseToRows,
  tablePrimaryKey = "id",
  tableName = "",
  datagridKey = "",
  showAddButton: showAddButtonProp = true,
  config = {},
  extraParams = {},
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationData, setPaginationData] = useState(DEFAULT_METADATA_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(Boolean(datagridKey));
  const [leadsSummary, setLeadsSummary] = useState(null);
  const [trainingProgramsAllOptions, setTrainingProgramsAllOptions] = useState([]);
  const [feePortalOpen, setFeePortalOpen] = useState(false);
  const [feePortalLoading, setFeePortalLoading] = useState(false);
  const [feePortalAdmission, setFeePortalAdmission] = useState(null);

  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const organizationId = resolveOrganizationId(userData);
  const organizationUserId = resolveOrganizationUserId(userData);
  const org = userData?.organization;
  const navigate = useNavigate();
  const fetchRequestSeqRef = useRef(0);
  const hasInitializedMetadataURL = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [feeInstallmentSearchInput, setFeeInstallmentSearchInput] = useState("");
  const [feeInstallmentSearch, setFeeInstallmentSearch] = useState("");
  const feeInstallmentFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/fee-installments")) return "";
    return [
      String(searchParams.get("period") || "").trim(),
      String(searchParams.get("due_period") || "").trim(),
      String(searchParams.get("month") || "").trim(),
      String(searchParams.get("status") || "").trim(),
      String(feeInstallmentSearch || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams, feeInstallmentSearch]);
  const studentsFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/students")) return "";
    return String(searchParams.get("admission_period") || "").trim();
  }, [apiEndpoint, searchParams]);
  const admissionsFetchKey = useMemo(() => {
    if (!apiEndpoint?.includes("/api/ems/admissions")) return "";
    return [
      String(searchParams.get("admission_period") || "").trim(),
      String(searchParams.get("training_program") || "").trim(),
    ].join("|");
  }, [apiEndpoint, searchParams]);

  const metadataListFetchKey = useMemo(() => {
    if (!useMetadataFilters) return "";
    return searchParams.toString();
  }, [useMetadataFilters, searchParams]);

  const resolvedDatagridKey = datagridKey || "";

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!organizationId || !resolvedDatagridKey) {
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${resolvedDatagridKey}_${organizationId}`;
        const requestPayload = {
          datagrid_key: datagridKey,
          organization_id: organizationId,
        };
        if (organizationUserId) {
          requestPayload.organization_user_id = organizationUserId;
        }

        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(requestPayload),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg && Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(serverCfg.columns);
          } else {
            setTableConfig(null);
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [organizationId, organizationUserId, resolvedDatagridKey]);

  useEffect(() => {
    if (!useMetadataFilters) return;
    if (hasInitializedMetadataURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    const metadataDefaults = resolveMetadataDefaultUrlParams(filterModule, tableName);
    Object.entries(metadataDefaults).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedMetadataURL.current = true;
  }, [useMetadataFilters, filterModule, tableName, searchParams, setSearchParams]);

  useEffect(() => {
    if (!apiEndpoint?.includes("/api/ems/fee-installments")) {
      setFeeInstallmentSearch("");
      return;
    }
    const timer = setTimeout(() => {
      setFeeInstallmentSearch(String(feeInstallmentSearchInput || "").trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [apiEndpoint, feeInstallmentSearchInput]);

  const basePath = addRoute || (editRouteBase || "").replace(/\/edit$/, "") || "";
  const actions = useMemo(() => getModuleActions(basePath), [basePath]);

  const hasPermission = (action) => checkPermission(Permission, action);

  const editDeleteActions = useMemo(() => {
    const edit = actions.edit || null;
    const del = actions.delete || null;
    if (!edit && !del) return undefined;
    return [edit, del];
  }, [actions.edit, actions.delete]);

  const showAddButton = showAddButtonProp && (actions.add ? hasPermission(actions.add) : true);
  const showViewButton =
    actions.view
      ? hasPermission(actions.view) || (actions.list ? hasPermission(actions.list) : false)
      : actions.list
        ? hasPermission(actions.list)
        : true;
  const showEditButton = actions.edit ? hasPermission(actions.edit) : true;
  const showDeleteButton = actions.delete ? hasPermission(actions.delete) : false;

  const isFeeInstallmentsList = basePath === "/fee-installments";
  const canAddStudentFee = hasPermission("ADD_STUDENT_FEE");
  const showReceiptButton =
    isFeeInstallmentsList ? showViewButton && canAddStudentFee : false;

  const canAddStudent = hasPermission("ADD_STUDENT");
  const canAddLeadActivity = hasPermission("ADD_LEAD_ACTIVITY");
  const canAddAdmission = hasPermission("ADD_ADMISSION");
  const canAddDemoParticipant = hasPermission("ADD_DEMO_SESSION");

  const handleReceipt = (row) => {
    // Prefill Fee Receipts form using query parameters.
    // Maps fee installment row fields -> fee receipt form fields.
    const params = new URLSearchParams();

    const studentId = row?.organization_ems_student_id;
    const admissionId = row?.organization_ems_admission_id;
    const feeInstallmentId =
      row?.organization_ems_fee_installment_id ?? row?.id;
    const studentCurrencyCode =
      row?.student_currency_code ?? row?.currency_code ?? row?.currency_code_inr;

    const setParamIfValid = (key, value) => {
      if (value === undefined || value === null) return;
      const s = String(value).trim();
      if (s === "" || s === "-") return;
      params.set(key, s);
    };

    if (studentId && studentId !== "-") {
      params.set("organization_ems_student_id", studentId);
    }
    if (admissionId && admissionId !== "-") {
      params.set("organization_ems_admission_id", admissionId);
    }
    if (feeInstallmentId && feeInstallmentId !== "-") {
      params.set("organization_ems_fee_installment_id", feeInstallmentId);
    }
    setParamIfValid("student_currency_code", studentCurrencyCode);
    // fee_instalment_inr -> amount_received_inr
    setParamIfValid(
      "amount_received_inr",
      row?.fee_instalment_inr ?? row?.fee_installment_inr
    );
    // fee_instalment_student_student_currency -> amount_paid_student_currency
    setParamIfValid(
      "amount_paid_student_currency",
      row?.fee_instalment_student_student_currency
    );
    setParamIfValid("payment_date", row?.payment_date_prefill);

    params.set("from_fee_installment", "1");

    const queryStr = params.toString();
    navigate(`/students-fees/add${queryStr ? `?${queryStr}` : ""}`);
  };

  const isLeadsList = basePath === "/leads";
  const showEnrolledButton = isLeadsList ? showViewButton && canAddStudent : false;
  const showLeadActivityButton =
    isLeadsList ? showViewButton && canAddLeadActivity : false;
  const canViewLeadActivity =
    hasPermission("VIEW_LEAD_ACTIVITY") || canAddLeadActivity;
  const showSnapshotButton =
    isLeadsList ? showViewButton && canViewLeadActivity : false;

  const handleLeadActivity = (row) => {
    const params = new URLSearchParams();
    const leadId =
      row?.organization_ems_lead_id ?? row?.id ?? row?.lead_id;
    if (
      leadId !== undefined &&
      leadId !== null &&
      String(leadId).trim() !== "" &&
      String(leadId) !== "-"
    ) {
      params.set("organization_ems_lead_id", String(leadId));
    }
    params.set("from_leads_list", "1");
    const q = params.toString();
    navigate(`/lead-activities/add${q ? `?${q}` : ""}`);
  };

  const handleEnrolled = (row) => {
    const params = new URLSearchParams();

    // Split full name -> first_name / last_name
    const fullName = String(row?.person_full_name ?? "").trim();
    const parts = fullName ? fullName.split(/\s+/).filter(Boolean) : [];
    const firstName = parts[0] || "";
    const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";

    if (firstName) params.set("first_name", firstName);
    if (lastName) params.set("last_name", lastName);

    // Address/location ids (should be present after we extend leads mapping)
    const countryId = row?.country_id;
    const stateId = row?.state_id;
    const cityId = row?.city_id;
    const isValidId = (v) => {
      if (v === undefined || v === null) return false;
      const s = String(v).trim();
      return s !== "" && s !== "-";
    };

    if (isValidId(countryId)) params.set("country_id", countryId);
    if (isValidId(stateId)) params.set("state_id", stateId);
    if (isValidId(cityId)) params.set("city_id", cityId);

    // Names fallback: avoids mismatched ids / slow dropdown mismatch.
    const stateName = row?.state_name;
    const cityName = row?.city_name;
    const isValidStr = (v) => {
      if (v === undefined || v === null) return false;
      const s = String(v).trim();
      return s !== "" && s !== "-";
    };

    if (isValidStr(stateName)) params.set("state_name", stateName);
    if (isValidStr(cityName)) params.set("city_name", cityName);

    // Contact fields
    const phone = row?.phone;
    const email = row?.email;
    const alternatePhone = row?.alternate_phone;
    if (phone && phone !== "-") params.set("phone", phone);
    if (email && email !== "-") params.set("email", email);
    if (alternatePhone && alternatePhone !== "-")
      params.set("alternate_phone", alternatePhone);

    // Optional fields
    const address = row?.address;
    const remarks = row?.remarks ?? row?.interested_program_remarks;
    if (address && address !== "-") params.set("address", address);
    if (remarks && remarks !== "-") params.set("remarks", remarks);

    const leadRowId =
      row?.organization_ems_lead_id ?? row?.id ?? row?.lead_id;
    if (
      leadRowId !== undefined &&
      leadRowId !== null &&
      String(leadRowId).trim() !== "" &&
      String(leadRowId) !== "-"
    ) {
      params.set("organization_ems_lead_id", String(leadRowId));
    }

    params.set("from_lead", "1");

    navigate(`/students/add${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const isStudentsList = basePath === "/students";
  const isAdmissionsList = basePath === "/admissions";
  const showFeePortalButton = isAdmissionsList && showViewButton;
  const showAdmissionButton =
    isStudentsList ? showViewButton && canAddAdmission : false;
  const isDemoSessionsList = basePath === "/demo-sessions";
  const canViewSelfDemoSessions = hasPermission("VIEW_SELF_DEMO_SESSIONS");
  const canViewAllDemoSessions = hasPermission("VIEW_DEMO_SESSIONS");
  const canViewSelfDemoParticipants = hasPermission("VIEW_SELF_DEMO_PARTICIPANTS");
  const canViewAllDemoParticipants = hasPermission("VIEW_DEMO_PARTICIPANTS");
  const canViewSelfBatches = hasPermission("VIEW_SELF_BATCHES");
  const canViewAllBatches = hasPermission("VIEW_BATCHES");
  const canViewSelfBatchStudents = hasPermission("VIEW_SELF_BATCH_STUDENTS");
  const canViewAllBatchStudents = hasPermission("VIEW_BATCH_STUDENTS");
  const canViewSelfBatchClasses = hasPermission("VIEW_SELF_BATCH_CLASSES");
  const canViewAllBatchClasses = hasPermission("VIEW_BATCH_CLASS");
  const canViewSelfBatchClassRecords = hasPermission("VIEW_SELF_BATCH_CLASS_RECORDS");
  const canViewAllBatchClassRecords = hasPermission("VIEW_BATCH_CLASS_RECORD");
  const canViewSelfClassAttendance = hasPermission("VIEW_SELF_CLASS_ATTENDANCE");
  const canViewAllClassAttendance = hasPermission("VIEW_CLASS_ATTENDANCES");
  const effectiveShowViewButton =
    isDemoSessionsList && canViewSelfDemoSessions
      ? showViewButton || canViewSelfDemoSessions
      : showViewButton;
  const showDemoParticipantButton =
    isDemoSessionsList ? effectiveShowViewButton && canAddDemoParticipant : false;
  const isBatchesList = basePath === "/batches";
  const isBatchClassesList = basePath === "/batch-classes";
  const isClassRecordsList = basePath === "/class-records";
  const canMarkClassAttendance =
    hasPermission("VIEW_SELF_CLASS_ATTENDANCE") ||
    hasPermission("VIEW_CLASS_ATTENDANCES") ||
    hasPermission("ADD_CLASS_ATTENDANCE") ||
    hasPermission("EDIT_CLASS_ATTENDANCE");
  const showBatchAttendanceButton =
    isClassRecordsList &&
    showViewButton &&
    canMarkClassAttendance;
  const canViewBatches = hasPermission("VIEW_BATCHES") || hasPermission("VIEW_SELF_BATCHES");
  const showBatchClassesButton =
    isBatchesList &&
    showViewButton &&
    canViewBatches;
  const canViewClassRecords =
    hasPermission("VIEW_BATCH_CLASS_RECORD") ||
    hasPermission("VIEW_SELF_BATCH_CLASS_RECORDS") ||
    hasPermission("VIEW_BATCH_CLASS") ||
    hasPermission("ADD_BATCH_CLASS") ||
    hasPermission("EDIT_BATCH_CLASS");
  const showBatchRecordsButton =
    isBatchClassesList &&
    showViewButton &&
    canViewClassRecords;
  const canViewBatchStudents =
    hasPermission("VIEW_BATCH_STUDENTS") ||
    hasPermission("VIEW_SELF_BATCH_STUDENTS") ||
    hasPermission("ADD_BATCH_STUDENT") ||
    hasPermission("EDIT_BATCH_STUDENTS");
  const showBatchStudentsButton =
    isBatchesList &&
    showViewButton &&
    canViewBatchStudents;

  const handleAdmission = (row) => {
    const hasLatestAdmissionDate =
      row?.latest_admission_date != null &&
      String(row.latest_admission_date).trim() !== "" &&
      String(row.latest_admission_date).trim() !== "-";
    const hasAnyAdmissions =
      Array.isArray(row?.student_admissions) && row.student_admissions.length > 0;

    if (hasLatestAdmissionDate || hasAnyAdmissions) {
      toast.error("Student is already enrolled.");
      return;
    }

    const params = new URLSearchParams();

    const studentId = row?.id ?? row?.organization_ems_student_id;
    const leadId =
      row?.organization_ems_lead_id ??
      row?.lead_id ??
      row?.organization_ems_lead_id;
    const trainingProgramId =
      row?.training_program_id ??
      row?.organization_ems_training_program_id ??
      row?.training_program?.organization_ems_training_program_id;

    if (studentId !== undefined && studentId !== null && String(studentId).trim() !== "" && String(studentId) !== "-") {
      params.set("organization_ems_student_id", studentId);
    }
    if (leadId !== undefined && leadId !== null && String(leadId).trim() !== "" && String(leadId) !== "-") {
      params.set("organization_ems_lead_id", leadId);
    }
    if (trainingProgramId !== undefined && trainingProgramId !== null && String(trainingProgramId).trim() !== "" && String(trainingProgramId) !== "-") {
      params.set("training_program_id", trainingProgramId);
    }

    params.set("from_student", "1");

    navigate(`/admissions/add${params.toString() ? `?${params.toString()}` : ""}`);
  };

  const handleFeePortal = async (row) => {
    const id = row?.id ?? row?.organization_ems_admission_id;
    if (
      id === undefined ||
      id === null ||
      String(id).trim() === "" ||
      String(id) === "-"
    ) {
      return;
    }
    setFeePortalOpen(true);
    setFeePortalLoading(true);
    setFeePortalAdmission(null);
    try {
      const res = await axios.get(`${MAIN_URL}/api/ems/admissions/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          organization_id: org?.organization_id,
        },
      });
      const payload = res.data?.data ?? res.data;
      setFeePortalAdmission(payload);
    } catch (err) {
      console.error("Fee portal fetch failed:", err);
      toast.error("Could not load fee details.");
      setFeePortalOpen(false);
    } finally {
      setFeePortalLoading(false);
    }
  };

  const handleBatchAttendanceMarking = (row) => {
    const id =
      row?.organization_ems_batch_class_id ??
      row?.batch_class?.organization_ems_batch_class_id ??
      row?.batchClass?.organization_ems_batch_class_id ??
      row?.id;
    if (
      id === undefined ||
      id === null ||
      String(id).trim() === "" ||
      String(id) === "-"
    ) {
      return;
    }
    const classRecordId =
      row?.organization_ems_batch_class_record_id ??
      row?.organization_ems_class_record_id ??
      row?.batch_record_id ??
      row?.id;
    const params = new URLSearchParams();
    if (
      isClassRecordsList &&
      classRecordId !== undefined &&
      classRecordId !== null &&
      String(classRecordId).trim() !== "" &&
      String(classRecordId) !== "-"
    ) {
      params.set("class_record_id", String(classRecordId));
    }
    navigate(
      `/batch-classes/attendance/${id}${
        params.toString() ? `?${params.toString()}` : ""
      }`
    );
  };
  const handleBatchClassesFromBatch = (row) => {
    const batchId =
      row?.organization_ems_batch_id ??
      row?.batch?.organization_ems_batch_id ??
      row?.id;
    if (
      batchId === undefined ||
      batchId === null ||
      String(batchId).trim() === "" ||
      String(batchId) === "-"
    ) {
      navigate("/batch-classes/add");
      return;
    }
    const params = new URLSearchParams();
    params.set("organization_ems_batch_id", String(batchId));
    navigate(`/batch-classes/add?${params.toString()}`);
  };
  const handleBatchRecordsFromBatchClass = (row) => {
    const batchId =
      row?.organization_ems_batch_id ??
      row?.batch?.organization_ems_batch_id ??
      "";
    const batchClassId =
      row?.organization_ems_batch_class_id ??
      row?.batch_class?.organization_ems_batch_class_id ??
      row?.batchClass?.organization_ems_batch_class_id ??
      row?.id ??
      "";
    const params = new URLSearchParams();
    if (
      batchId !== undefined &&
      batchId !== null &&
      String(batchId).trim() !== "" &&
      String(batchId) !== "-"
    ) {
      params.set("organization_ems_batch_id", String(batchId));
    }
    if (
      batchClassId !== undefined &&
      batchClassId !== null &&
      String(batchClassId).trim() !== "" &&
      String(batchClassId) !== "-"
    ) {
      params.set("organization_ems_batch_class_id", String(batchClassId));
    }
    navigate(`/class-records/add${params.toString() ? `?${params.toString()}` : ""}`);
  };
  const handleBatchStudentsFromBatch = (row) => {
    const batchId =
      row?.organization_ems_batch_id ??
      row?.batch?.organization_ems_batch_id ??
      row?.id;
    if (
      batchId === undefined ||
      batchId === null ||
      String(batchId).trim() === "" ||
      String(batchId) === "-"
    ) {
      navigate("/batch-students/add");
      return;
    }
    const params = new URLSearchParams();
    params.set("organization_ems_batch_id", String(batchId));
    navigate(`/batch-students/add?${params.toString()}`);
  };

  const handleDemoParticipant = (row) => {
    const params = new URLSearchParams();
    const demoSessionId =
      row?.organization_ems_demo_session_id ?? row?.id;
    if (
      demoSessionId !== undefined &&
      demoSessionId !== null &&
      String(demoSessionId).trim() !== "" &&
      String(demoSessionId) !== "-"
    ) {
      params.set("organization_ems_demo_session_id", demoSessionId);
    }
    navigate(
      `/demo-participants/add${params.toString() ? `?${params.toString()}` : ""}`
    );
  };

  const handleMetadataSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleMetadataPageChange = useCallback(
    (page, pageSize) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(page));
        next.set("pageSize", String(pageSize));
        return next;
      });
    },
    [setSearchParams]
  );

  // ✅ Fetch data (generic)
  const fetchData = async (loadingVal = true) => {
    if (!apiEndpoint) return;
    const requestSeq = ++fetchRequestSeqRef.current;
    setLoading(loadingVal);
    try {
      if (useMetadataFilters && filteredApiEndpoint && organizationId) {
        const filters = buildMetadataFiltersFromSearchParams(searchParams);
        const search = searchParams.get("search");
        const defaultSortField = resolveMetadataDefaultSortField(
          filterModule,
          tableName
        );
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 25,
          organization_id: organizationId,
          sort: {
            field: searchParams.get("sortBy") || defaultSortField,
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" ? { search: search.trim() } : {}),
        };

        if (filterModule === "ems_leads" || tableName === "ems_leads") {
          Object.assign(requestBody, {
            training_program: searchParams.get("training_program"),
            lead_stage: searchParams.get("lead_stage"),
            lead_status: searchParams.has("lead_status")
              ? searchParams.get("lead_status")
              : "",
            period: searchParams.get("period"),
            month: searchParams.get("month"),
            lead_date: searchParams.get("lead_date"),
          });
        }

        if (filterModule === "ems_demo_sessions" || tableName === "ems_demo_sessions") {
          Object.assign(requestBody, {
            period: searchParams.get("period"),
            month: searchParams.get("month"),
          });
        }

        if (filterModule === "ems_students" || tableName === "ems_students") {
          Object.assign(requestBody, {
            admission_period: searchParams.get("admission_period"),
            training_program: searchParams.get("training_program"),
            student_status: searchParams.get("student_status"),
          });
        }

        if (filterModule === "ems_admissions" || tableName === "ems_admissions") {
          Object.assign(requestBody, {
            admission_period: searchParams.get("admission_period"),
            training_program: searchParams.get("training_program"),
            period: searchParams.get("period"),
            month: searchParams.get("month"),
            enrolled: searchParams.get("enrolled"),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
          });
        }

        if (
          filterModule === "ems_fee_installments" ||
          tableName === "ems_fee_installments"
        ) {
          Object.assign(requestBody, {
            period: searchParams.get("period"),
            month: searchParams.get("month"),
            status: searchParams.get("status"),
            due_period: searchParams.get("due_period"),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_fee_installment_id: searchParams.get(
              "organization_ems_fee_installment_id"
            ),
          });
          const feeSearch = String(feeInstallmentSearch || "").trim();
          if (feeSearch) {
            requestBody.search = feeSearch;
          }
        }

        if (
          filterModule === "ems_student_fees" ||
          tableName === "ems_student_fees"
        ) {
          Object.assign(requestBody, {
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_fee_installment_id: searchParams.get(
              "organization_ems_fee_installment_id"
            ),
          });
        }

        if (filterModule === "ems_batches" || tableName === "ems_batches") {
          Object.assign(requestBody, {
            training_program: searchParams.get("training_program"),
            status: searchParams.get("status"),
            mode: searchParams.get("mode"),
          });
        }

        if (
          filterModule === "ems_batch_students" ||
          tableName === "ems_batch_students"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_id: searchParams.get("organization_ems_batch_id"),
            training_program: searchParams.get("training_program"),
            batch_status: searchParams.has("batch_status")
              ? searchParams.get("batch_status")
              : "Pursuing",
            mode: searchParams.get("mode"),
          });
        }

        if (
          filterModule === "ems_batch_classes" ||
          tableName === "ems_batch_classes"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_id: searchParams.get("organization_ems_batch_id"),
            training_program: searchParams.get("training_program"),
            class_status: searchParams.get("class_status") || searchParams.get("status"),
            mode: searchParams.get("mode"),
          });
        }

        if (
          filterModule === "ems_batch_class_records" ||
          tableName === "ems_batch_class_records"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_id: searchParams.get("organization_ems_batch_id"),
            organization_ems_batch_class_id: searchParams.get(
              "organization_ems_batch_class_id"
            ),
            training_program: searchParams.get("training_program"),
            status: searchParams.get("status"),
            mode: searchParams.get("mode"),
          });
        }

        if (
          filterModule === "ems_class_attendance" ||
          tableName === "ems_class_attendance"
        ) {
          Object.assign(requestBody, {
            organization_ems_batch_class_id: searchParams.get(
              "organization_ems_batch_class_id"
            ),
            organization_ems_batch_class_record_id:
              searchParams.get("organization_ems_batch_class_record_id") ||
              searchParams.get("class_record_id"),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            attendance_status: searchParams.get("attendance_status"),
          });
        }

        if (
          filterModule === "ems_assessments" ||
          tableName === "ems_assessments"
        ) {
          Object.assign(requestBody, {
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            organization_ems_batch_id: searchParams.get(
              "organization_ems_batch_id"
            ),
            assessment_type: searchParams.get("assessment_type"),
            status: searchParams.get("status"),
          });
        }

        if (
          filterModule === "ems_assessment_results" ||
          tableName === "ems_assessment_results"
        ) {
          Object.assign(requestBody, {
            organization_ems_assessment_id: searchParams.get(
              "organization_ems_assessment_id"
            ),
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            result_status: searchParams.get("result_status"),
          });
        }

        if (
          filterModule === "ems_training_programs" ||
          tableName === "ems_training_programs"
        ) {
          Object.assign(requestBody, {
            organization_ems_training_program_category_id: searchParams.get(
              "organization_ems_training_program_category_id"
            ),
            training_program_category_id: searchParams.get(
              "training_program_category_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            is_active: searchParams.get("is_active"),
          });
        }

        if (
          filterModule === "ems_certificates" ||
          tableName === "ems_certificates"
        ) {
          Object.assign(requestBody, {
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_batch_id: searchParams.get(
              "organization_ems_batch_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            certificate_status: searchParams.get("certificate_status"),
          });
        }

        if (
          filterModule === "ems_recruitment_agencies" ||
          tableName === "ems_recruitment_agencies"
        ) {
          Object.assign(requestBody, {
            status: searchParams.get("status"),
            country_id: searchParams.get("country_id"),
            state_id: searchParams.get("state_id"),
            city_id: searchParams.get("city_id"),
          });
        }

        if (
          filterModule === "ems_companies" ||
          tableName === "ems_companies"
        ) {
          Object.assign(requestBody, {
            status: searchParams.get("status"),
            industry: searchParams.get("industry"),
          });
        }

        if (
          filterModule === "ems_placement_referrals" ||
          tableName === "ems_placement_referrals"
        ) {
          Object.assign(requestBody, {
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            training_program: searchParams.get("training_program"),
            training_program_id: searchParams.get("training_program_id"),
            organization_ems_recruitment_agency_id: searchParams.get(
              "organization_ems_recruitment_agency_id"
            ),
            organization_ems_company_id: searchParams.get(
              "organization_ems_company_id"
            ),
            referral_status: searchParams.get("referral_status"),
          });
        }

        if (
          filterModule === "ems_student_reviews" ||
          tableName === "ems_student_reviews"
        ) {
          Object.assign(requestBody, {
            organization_ems_student_id: searchParams.get(
              "organization_ems_student_id"
            ),
            organization_ems_admission_id: searchParams.get(
              "organization_ems_admission_id"
            ),
            organization_ems_student_review_platform_id: searchParams.get(
              "organization_ems_student_review_platform_id"
            ),
            review_status: searchParams.get("review_status"),
          });
        }

        if (
          filterModule === "ems_lead_stages" ||
          tableName === "ems_lead_stages"
        ) {
          Object.assign(requestBody, {
            lead_status: searchParams.get("lead_status"),
          });
        }

        if (
          filterModule === "ems_training_spaces" ||
          tableName === "ems_training_spaces"
        ) {
          Object.assign(requestBody, {
            mode: searchParams.get("mode"),
            status: searchParams.get("status"),
          });
        }

        const leadIdParam = searchParams.get("organization_ems_lead_id");
        const isLeadScopedMetadataList =
          filterModule === "ems_lead_activities" ||
          tableName === "ems_lead_activities" ||
          filterModule === "ems_lead_contact_timings" ||
          tableName === "ems_lead_contact_timings" ||
          filterModule === "ems_demo_participants" ||
          tableName === "ems_demo_participants";
        if (
          isLeadScopedMetadataList &&
          leadIdParam &&
          String(leadIdParam).trim() !== ""
        ) {
          requestBody.organization_ems_lead_id = String(leadIdParam).trim();
        }

        const demoSessionIdParam = searchParams.get("organization_ems_demo_session_id");
        const isDemoSessionScopedMetadataList =
          filterModule === "ems_demo_participants" ||
          tableName === "ems_demo_participants";
        if (
          isDemoSessionScopedMetadataList &&
          demoSessionIdParam &&
          String(demoSessionIdParam).trim() !== ""
        ) {
          requestBody.organization_ems_demo_session_id =
            String(demoSessionIdParam).trim();
        }

        const response = await fetch(`${MAIN_URL}${filteredApiEndpoint}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            "X-Application-Key": "ems",
          },
          body: JSON.stringify(requestBody),
        });

        if (requestSeq !== fetchRequestSeqRef.current) return;

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const rawRows = result?.data ?? [];
        const mappedData = mapResponseToRows
          ? mapResponseToRows(Array.isArray(rawRows) ? rawRows : [])
          : rawRows;

        setData(Array.isArray(mappedData) ? mappedData : []);

        if (result?.pagination) {
          const pageSize =
            result.pagination.pageSize || result.pagination.limit || 25;
          setPaginationData({
            page: result.pagination.page || 1,
            pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          });
        } else {
          setPaginationData(DEFAULT_METADATA_PAGINATION);
        }

        return;
      }

      const feeInstallmentParams = apiEndpoint?.includes("/api/ems/fee-installments")
        ? (() => {
            const period = String(searchParams.get("period") || "").trim();
            const month = String(searchParams.get("month") || "").trim();
            const shouldSendPeriod = !(month && period.toLowerCase() === "month");
            const status = String(searchParams.get("status") || "").trim().toLowerCase();
            return {
              ...(shouldSendPeriod && period ? { period } : null),
            ...(String(searchParams.get("due_period") || "").trim()
              ? { due_period: String(searchParams.get("due_period")).trim() }
              : null),
              ...(month ? { month } : null),
            ...(status ? { status } : null),
            ...(String(feeInstallmentSearch || "").trim()
              ? { search: String(feeInstallmentSearch).trim() }
              : null),
            };
          })()
        : {};
      const studentsParams = apiEndpoint?.includes("/api/ems/students")
        ? {
            ...(String(searchParams.get("admission_period") || "").trim()
              ? { admission_period: String(searchParams.get("admission_period")).trim() }
              : null),
          }
        : {};
      const admissionsTrainingProgramFilter = String(
        searchParams.get("training_program") || ""
      ).trim();
      const admissionsParams = apiEndpoint?.includes("/api/ems/admissions")
        ? {
            ...(String(searchParams.get("admission_period") || "").trim()
              ? { admission_period: String(searchParams.get("admission_period")).trim() }
              : null),
            ...(admissionsTrainingProgramFilter
              ? {
                  // Dashboard deep links can pass either program name or program ID.
                  training_program: admissionsTrainingProgramFilter,
                  ...(String(Number(admissionsTrainingProgramFilter)) ===
                  admissionsTrainingProgramFilter
                    ? { training_program_id: admissionsTrainingProgramFilter }
                    : null),
                }
              : null),
          }
        : {};
      const response = await axios.get(`${MAIN_URL}${apiEndpoint}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          per_page: "all",
          organization_id: organizationId,
          ...extraParams,
          ...feeInstallmentParams,
          ...studentsParams,
          ...admissionsParams,
        },
      });

      if (requestSeq !== fetchRequestSeqRef.current) return;

      const rawPayload = response.data?.data ?? response.data;
      const responseData = normalizeListPayload(rawPayload);
      const sortedResponseData =
        apiEndpoint?.includes("/api/ems/leads") && Array.isArray(responseData)
          ? [...responseData].sort((a, b) => {
              const ad = new Date(a?.lead_datetime || 0).getTime();
              const bd = new Date(b?.lead_datetime || 0).getTime();
              return bd - ad;
            })
          : apiEndpoint?.includes("/api/ems/lead-activities") &&
              Array.isArray(responseData)
            ? [...responseData].sort((a, b) => {
                const ad = new Date(a?.activity_datetime || 0).getTime();
                const bd = new Date(b?.activity_datetime || 0).getTime();
                if (bd !== ad) return bd - ad;
                const ai =
                  Number(a?.organization_ems_lead_activity_id ?? a?.id ?? 0) || 0;
                const bi =
                  Number(b?.organization_ems_lead_activity_id ?? b?.id ?? 0) || 0;
                return bi - ai;
              })
            : responseData;
      const mappedData = mapResponseToRows
        ? mapResponseToRows(sortedResponseData)
        : sortedResponseData;

      setData(mappedData);

    } catch (err) {
      if (requestSeq !== fetchRequestSeqRef.current) return;
      console.error(`Error fetching ${title}:`, err);
    } finally {
      if (requestSeq === fetchRequestSeqRef.current) {
        setLoading(false);
      }
    }
  };

  // Run on mount and pagination change
  useEffect(() => {
    if (!organizationId) return;
    if (useMetadataFilters && !hasInitializedMetadataURL.current) return;

    setData([]);
    fetchData(true);
  }, [
    organizationId,
    apiEndpoint,
    filteredApiEndpoint,
    useMetadataFilters,
    metadataListFetchKey,
    feeInstallmentFetchKey,
    studentsFetchKey,
    admissionsFetchKey,
  ]);

  useEffect(() => {
    if (!apiEndpoint?.includes("/api/ems/leads") || !organizationId) {
      setLeadsSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/leads-summary`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { organization_id: organizationId },
        });
        if (cancelled) return;
        setLeadsSummary(response.data?.data ?? null);
      } catch (err) {
        console.error("Error fetching leads summary:", err);
        if (!cancelled) setLeadsSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, organizationId]);

  useEffect(() => {
    const needsTrainingProgramOptions =
      apiEndpoint?.includes("/api/ems/batch-students") ||
      apiEndpoint?.includes("/api/ems/students") ||
      apiEndpoint?.includes("/api/ems/admissions") ||
      apiEndpoint?.includes("/api/ems/leads") ||
      apiEndpoint?.includes("/api/ems/batches") ||
      apiEndpoint?.includes("/api/ems/class-records");
    if (!needsTrainingProgramOptions || !org?.organization_id) {
      setTrainingProgramsAllOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/training-programs`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            per_page: "all",
            organization_id: org.organization_id,
            ...extraParams,
          },
        });
        const rawPayload = response.data?.data ?? response.data;
        const arr = normalizeListPayload(rawPayload);
        if (cancelled) return;
        const seen = new Set();
        const options = arr
          .map((item) => ({
            id:
              item?.organization_ems_training_program_id ??
              item?.training_program_id ??
              item?.id,
            name: String(item?.training_program_name || "").trim(),
          }))
          .filter((s) => s.id != null && s.name && s.name !== "-")
          .filter((s) => {
            const key = `${String(s.id).trim()}::${s.name.toLowerCase()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        setTrainingProgramsAllOptions(options);
      } catch (err) {
        console.error("Error fetching training programs for filter:", err);
        if (!cancelled) setTrainingProgramsAllOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiEndpoint, org?.organization_id]);

  const periodFilter = searchParams.get("period");
  const batchIdFilter = searchParams.get("organization_ems_batch_id");
  const trainingProgramFilter = searchParams.get("training_program");
  const trainingProgramCategoryFilter = searchParams.get("training_program_category");
  const leadStageFilter = searchParams.get("lead_stage");
  const leadDateFilter = searchParams.get("lead_date");
  const monthFilter = searchParams.get("month");
  const enrolledFilter = searchParams.get("enrolled");
  const statusFilter = searchParams.get("status");
  const duePeriodFilter = searchParams.get("due_period");
  const attendanceStatusFilter = searchParams.get("attendance_status");
  const batchStatusFilter = searchParams.get("batch_status");
  const batchModeFilter = searchParams.get("mode");
  const leadStatusFilter = searchParams.get("lead_status");
  const leadActivityLeadIdFilter = searchParams.get("organization_ems_lead_id");
  const resolvedTableConfig = useMemo(() => {
    let baseConfig = config;

    if (apiEndpoint?.includes("/api/ems/leads")) {
      baseConfig = {
        ...config,
        pagination: {
          ...(config?.pagination || {}),
          rowsPerPage: config?.pagination?.rowsPerPage || 25,
        },
        sort: {
          field:
            config?.sort?.field && config?.sort?.direction
              ? config.sort.field
              : "lead_datetime",
          direction:
            config?.sort?.field && config?.sort?.direction
              ? config.sort.direction
              : "desc",
        },
      };
    }

    return tableConfig ? { ...baseConfig, ...tableConfig } : baseConfig;
  }, [apiEndpoint, config, tableConfig]);

  const rowsForTable = useMemo(() => {
    if (apiEndpoint?.includes("/api/ems/demo-sessions")) {
      let filtered = data;
      if (!useMetadataFilters) {
        if (periodFilter === "today") {
          filtered = filterDemoSessionsRowsToday(filtered);
        }
        if (periodFilter === "month") {
          filtered = filterDemoSessionsRowsByMonth(filtered, monthFilter);
        }
      }
      // Self-demo permission: only show trainer-matched sessions for logged-in user.
      if (canViewSelfDemoSessions && !canViewAllDemoSessions) {
        const selfRows = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
        return selfRows;
      }
      return filtered;
    }
    if (apiEndpoint?.includes("/api/ems/demo-participants")) {
      let filtered = data;
      if (canViewSelfDemoParticipants && !canViewAllDemoParticipants) {
        filtered = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/admissions")) {
      let filtered = data;
      if (!useMetadataFilters) {
        if (periodFilter === "today") {
          filtered = filterAdmissionsRowsAdmissionToday(filtered);
        }
        if (periodFilter === "month") {
          filtered = filterAdmissionsRowsByMonth(filtered, monthFilter);
        }

        // enrolled=1 -> only show admissions that represent enrollments
        if (String(enrolledFilter || "") === "1") {
          const allowed = new Set(["confirmed", "completed"]);
          filtered = filtered.filter((row) =>
            allowed.has(String(row?.admission_status || "").trim().toLowerCase())
          );
        }
      }

      if (!useMetadataFilters && trainingProgramFilter) {
        const selectedProgramId = String(trainingProgramFilter).trim();
        const selectedProgramIdIsNumeric =
          String(Number(selectedProgramId)) === selectedProgramId;
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === selectedProgramId
        );
        const selectedProgramName = String(
          selectedProgram?.name ??
            // Dashboard graph deep links may pass program name instead of ID.
            (selectedProgramIdIsNumeric ? "" : selectedProgramId)
        )
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const rowProgramId = String(
            row?.training_program_id ??
              row?.organization_ems_training_program_id ??
              row?.training_program?.organization_ems_training_program_id ??
              ""
          ).trim();
          const rowProgramName = String(
            row?.training_program ??
              row?.training_program?.training_program_name ??
              ""
          )
            .trim()
            .toLowerCase();
          return (
            (rowProgramId && rowProgramId === selectedProgramId) ||
            (selectedProgramName &&
              (rowProgramName === selectedProgramName ||
                rowProgramName.includes(selectedProgramName)))
          );
        });
      }

      if (trainingProgramCategoryFilter) {
        const q = String(trainingProgramCategoryFilter).trim().toLowerCase();
        const hasCategoryData = filtered.some((row) => {
          const v = String(row?.training_program_category || "").trim();
          return v && v !== "-";
        });

        // Apply category filter only when category values are actually present.
        // If category is unavailable in admissions payload, don't hide valid program matches.
        if (hasCategoryData) {
          filtered = filtered.filter((row) =>
            String(row?.training_program_category || "")
              .trim()
              .toLowerCase()
              .includes(q)
          );
        }
      }

      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/fee-installments")) {
      if (useMetadataFilters) {
        return data;
      }

      // When status/period/month/due_period are in the URL, backend already filtered —
      // use that payload as-is so stale client rows are not re-filtered incorrectly.
      let filtered = data;
      const period = String(periodFilter || "").trim().toLowerCase();
      const month = String(monthFilter || "").trim();
      const status = String(statusFilter || "").trim().toLowerCase();
      const duePeriod = String(duePeriodFilter || "").trim().toLowerCase();
      const usesServerFilters =
        Boolean(status) || Boolean(period) || Boolean(month) || Boolean(duePeriod);

      if (!usesServerFilters) {
        if (month) {
          filtered = filterFeeInstallmentsByDueMonth(filtered, month);
        } else if (period === "today" || period === "month") {
          filtered = filterFeeInstallmentsByDuePeriod(filtered, period);
        } else if (period === "overdue") {
          filtered = filterFeeInstallmentsOverdue(filtered);
        }
      } else if (month) {
        filtered = filterFeeInstallmentsByDueMonth(filtered, month);
      }

      if (status && !usesServerFilters) {
        if (status === "overdue") {
          filtered = filterFeeInstallmentsOverdue(filtered);
        } else {
          filtered = filtered.filter(
            (row) => String(row?.status ?? "").trim().toLowerCase() === status
          );
        }
      }

      // Sorting rule:
      // 1) Pending installments first
      // 2) Within Pending, due-date nearest to today first
      // 3) Remaining rows by due date ascending
      const today = new Date();
      const startOfToday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      ).getTime();
      const parseDueTs = (row) => {
        const raw =
          row?.payment_date_prefill ??
          row?.due_date ??
          row?.due_date_raw ??
          null;
        const ts = raw ? new Date(raw).getTime() : Number.NaN;
        return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY;
      };
      return [...filtered].sort((a, b) => {
        const aPending = String(a?.status ?? "").trim().toLowerCase() === "pending";
        const bPending = String(b?.status ?? "").trim().toLowerCase() === "pending";
        if (aPending !== bPending) return aPending ? -1 : 1;

        const ad = parseDueTs(a);
        const bd = parseDueTs(b);

        if (aPending && bPending) {
          const aDiff = Math.abs(ad - startOfToday);
          const bDiff = Math.abs(bd - startOfToday);
          if (aDiff !== bDiff) return aDiff - bDiff;
          return ad - bd;
        }

        return ad - bd;
      });
    }

    if (
      apiEndpoint?.includes("/api/ems/lead-activities") &&
      !useMetadataFilters
    ) {
      let filtered = data;
      if (
        leadActivityLeadIdFilter !== null &&
        String(leadActivityLeadIdFilter).trim() !== ""
      ) {
        const q = String(leadActivityLeadIdFilter).trim();
        filtered = filtered.filter((row) =>
          String(row?.organization_ems_lead_id ?? "").trim() === q
        );
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/batches")) {
      let filtered = data;
      if (canViewSelfBatches && !canViewAllBatches) {
        filtered = filtered.filter((row) =>
          emsBatchListRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
        if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
          const selectedProgram = (trainingProgramsAllOptions || []).find(
            (p) => String(p?.id ?? "").trim() === String(trainingProgramFilter).trim()
          );
          const selectedProgramName = String(selectedProgram?.name ?? "")
            .trim()
            .toLowerCase();
          if (selectedProgramName) {
            filtered = filtered.filter(
              (row) =>
                String(row?.training_program ?? "").trim().toLowerCase() === selectedProgramName
            );
          }
        }
        if (statusFilter != null && String(statusFilter).trim() !== "") {
          const q = String(statusFilter).trim().toLowerCase();
          filtered = filtered.filter(
            (row) => String(row?.status ?? "").trim().toLowerCase() === q
          );
        }
        if (batchModeFilter != null && String(batchModeFilter).trim() !== "") {
          const q = String(batchModeFilter).trim().toLowerCase();
          filtered = filtered.filter(
            (row) =>
              String(row?.batch_mode ?? row?.mode ?? "")
                .trim()
                .toLowerCase() === q
          );
        }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/batch-classes")) {
      let filtered = data;
      if (canViewSelfBatchClasses && !canViewAllBatchClasses) {
        filtered = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (batchIdFilter != null && String(batchIdFilter).trim() !== "") {
        const q = String(batchIdFilter).trim();
        filtered = filtered.filter(
          (row) =>
            String(
              row?.organization_ems_batch_id ??
                row?.batch?.organization_ems_batch_id ??
                ""
            ).trim() === q
        );
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/batch-students")) {
      let filtered = data;
      if (canViewSelfBatchStudents && !canViewAllBatchStudents) {
        filtered = filtered.filter((row) =>
          emsBatchListRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (batchIdFilter != null && String(batchIdFilter).trim() !== "") {
        const q = String(batchIdFilter).trim();
        filtered = filtered.filter(
          (row) =>
            String(
              row?.organization_ems_batch_id ??
                row?.batch?.organization_ems_batch_id ??
                ""
            ).trim() === q
        );
      }
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const q = String(trainingProgramFilter).trim();
        filtered = filtered.filter(
          (row) => String(row?.training_program_id ?? "").trim() === q
        );
      }
      if (batchModeFilter != null && String(batchModeFilter).trim() !== "") {
        const q = String(batchModeFilter).trim().toLowerCase();
        filtered = filtered.filter(
          (row) => String(row?.mode ?? "").trim().toLowerCase() === q
        );
      }
      {
        const rawStatus = String(batchStatusFilter ?? "").trim().toLowerCase();
        const effectiveStatus = rawStatus === "" ? "pursuing" : rawStatus;
        if (effectiveStatus !== "all") {
          filtered = filtered.filter(
            (row) =>
              String(row?.batch_status ?? "").trim().toLowerCase() ===
              effectiveStatus
          );
        }
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/class-records")) {
      let filtered = data;
      if (canViewSelfBatchClassRecords && !canViewAllBatchClassRecords) {
        filtered = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const q = String(trainingProgramFilter).trim().toLowerCase();
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === String(trainingProgramFilter).trim()
        );
        const selectedProgramName = String(selectedProgram?.name ?? "")
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const name = String(row?.training_program ?? "").trim().toLowerCase();
          if (!name) return false;
          return name === q || (selectedProgramName && name === selectedProgramName);
        });
      }
      if (statusFilter != null && String(statusFilter).trim() !== "") {
        const q = String(statusFilter).trim().toLowerCase();
        filtered = filtered.filter(
          (row) => String(row?.status ?? "").trim().toLowerCase() === q
        );
      }
      if (batchIdFilter != null && String(batchIdFilter).trim() !== "") {
        const q = String(batchIdFilter).trim();
        filtered = filtered.filter(
          (row) =>
            String(
              row?.organization_ems_batch_id ??
                row?.batch?.organization_ems_batch_id ??
                ""
            ).trim() === q
        );
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/classes-attendance")) {
      let filtered = data;
      if (canViewSelfClassAttendance && !canViewAllClassAttendance) {
        filtered = filtered.filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        );
      }
      if (!useMetadataFilters) {
      if (
        attendanceStatusFilter != null &&
        String(attendanceStatusFilter).trim() !== ""
      ) {
        const q = String(attendanceStatusFilter).trim().toLowerCase();
        filtered = filtered.filter(
          (row) =>
            String(row?.attendance_status ?? "").trim().toLowerCase() === q
        );
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/students")) {
      let filtered = data;
      if (!useMetadataFilters) {
      const studentStatusFilter = searchParams.get("student_status");
      if (studentStatusFilter != null && String(studentStatusFilter).trim() !== "") {
        const q = String(studentStatusFilter).trim().toLowerCase();
        filtered = filtered.filter(
          (row) => String(row?.student_status ?? "").trim().toLowerCase() === q
        );
      }
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const selectedProgramId = String(trainingProgramFilter).trim();
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === selectedProgramId
        );
        const selectedProgramName = String(selectedProgram?.name ?? "")
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const programIds = [
            row?.student_training_program_id,
            row?.training_program_id,
            ...(Array.isArray(row?.student_admissions)
              ? row.student_admissions.map(
                  (a) =>
                    a?.training_program_id ??
                    a?.training_program?.organization_ems_training_program_id ??
                    a?.training_program?.training_program_id
                )
              : []),
            ...(Array.isArray(row?.studnet_batches)
              ? row.studnet_batches.map(
                  (sb) =>
                    sb?.batch?.training_program_id ??
                    sb?.training_program_id ??
                    sb?.batch?.training_program?.organization_ems_training_program_id
                )
              : []),
          ]
            .map((v) => String(v ?? "").trim())
            .filter(Boolean);

          const programNames = [
            row?.student_training_program,
            ...(Array.isArray(row?.student_admissions)
              ? row.student_admissions.map((a) => a?.training_program?.training_program_name)
              : []),
            ...(Array.isArray(row?.studnet_batches)
              ? row.studnet_batches.map((sb) => sb?.batch?.training_program?.training_program_name)
              : []),
          ]
            .map((v) => String(v ?? "").trim().toLowerCase())
            .filter(Boolean);

          return (
            programIds.includes(selectedProgramId) ||
            (selectedProgramName && programNames.includes(selectedProgramName))
          );
        });
      }
      }
      return filtered;
    }

    if (apiEndpoint?.includes("/api/ems/leads") && !useMetadataFilters) {
      let filtered = data;
      if (periodFilter === "today") {
        filtered = filterLeadsRowsByPeriod(filtered, periodFilter);
      }
      if (periodFilter === "month") {
        filtered = monthFilter
          ? filterLeadsRowsByMonth(filtered, monthFilter)
          : filterLeadsRowsByPeriod(filtered, periodFilter);
      }
      if (leadStageFilter) {
        const q = String(leadStageFilter).trim().toLowerCase();
        filtered = filtered.filter((row) => {
          const stageName = String(row?.lead_stage_name || "")
            .trim()
            .toLowerCase();
          const stageId = String(row?.lead_stage_id ?? "")
            .trim()
            .toLowerCase();
          return stageName === q || stageId === q;
        });
      }
      if (trainingProgramFilter != null && String(trainingProgramFilter).trim() !== "") {
        const q = String(trainingProgramFilter).trim().toLowerCase();
        const selectedProgram = (trainingProgramsAllOptions || []).find(
          (p) => String(p?.id ?? "").trim() === String(trainingProgramFilter).trim()
        );
        const selectedProgramName = String(selectedProgram?.name ?? "")
          .trim()
          .toLowerCase();
        filtered = filtered.filter((row) => {
          const name = String(row?.training_program ?? "").trim().toLowerCase();
          if (!name) return false;
          return name === q || (selectedProgramName && name === selectedProgramName);
        });
      }
      if (leadDateFilter) {
        const target = parseLeadDateFilter(leadDateFilter);
        if (target && !Number.isNaN(target.getTime())) {
          filtered = filtered.filter((row) => {
            const dt = parseLeadListDateTime(row.lead_datetime);
            if (!dt || Number.isNaN(dt.getTime())) return false;
            return (
              dt.getFullYear() === target.getFullYear() &&
              dt.getMonth() === target.getMonth() &&
              dt.getDate() === target.getDate()
            );
          });
        }
      }
      {
        const trimmedStatus =
          leadStatusFilter != null ? String(leadStatusFilter).trim() : "";
        // Default (no param) = Open. Explicit "all" shows every status.
        const statusFilterAll = trimmedStatus.toLowerCase() === "all";
        if (!statusFilterAll) {
          const q =
            trimmedStatus === ""
              ? "open"
              : String(trimmedStatus).trim().toLowerCase();
          filtered = filtered.filter((row) => {
            const status = String(row?.__lead_status_display ?? "")
              .trim()
              .toLowerCase();
            const normalizedStatus = status === "close" ? "closed" : status;
            const normalizedQuery = q === "close" ? "closed" : q;
            return normalizedStatus === normalizedQuery;
          });
        }
      }
      return filtered;
    }

    return data;
  }, [
    data,
    periodFilter,
    batchIdFilter,
    apiEndpoint,
    trainingProgramFilter,
    trainingProgramCategoryFilter,
    trainingProgramsAllOptions,
    leadStageFilter,
    trainingProgramsAllOptions,
    leadDateFilter,
    monthFilter,
    enrolledFilter,
    statusFilter,
    duePeriodFilter,
    attendanceStatusFilter,
    batchStatusFilter,
    batchModeFilter,
    leadStatusFilter,
    leadActivityLeadIdFilter,
    canViewSelfDemoSessions,
    canViewAllDemoSessions,
    canViewSelfDemoParticipants,
    canViewAllDemoParticipants,
    canViewSelfBatches,
    canViewAllBatches,
    canViewSelfBatchStudents,
    canViewAllBatchStudents,
    canViewSelfBatchClasses,
    canViewAllBatchClasses,
    canViewSelfBatchClassRecords,
    canViewAllBatchClassRecords,
    canViewSelfClassAttendance,
    canViewAllClassAttendance,
    userData,
    useMetadataFilters,
  ]);

  const isLeadsApiList = apiEndpoint?.includes("/api/ems/leads");
  const isAdmissionsApiList = apiEndpoint?.includes("/api/ems/admissions");
  const leadsListSubtitle = useMemo(() => {
    if (!isLeadsApiList) return null;
    const total =
      leadsSummary?.total_leads ?? paginationData?.total ?? 0;
    const enrolled =
      leadsSummary?.leads_funnel_metrics?.enrolled_leads_this_month?.current ?? 0;
    return `${total} total leads • ${enrolled} enrolled this cycle`;
  }, [isLeadsApiList, leadsSummary, paginationData?.total]);
  const isBatchesApiList = apiEndpoint?.includes("/api/ems/batches");
  const isBatchStudentsApiList = apiEndpoint?.includes("/api/ems/batch-students");
  const isClassRecordsApiList = apiEndpoint?.includes("/api/ems/class-records");
  const isClassesAttendanceApiList =
    apiEndpoint?.includes("/api/ems/classes-attendance");
  const selectedBatchTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedBatchStatus = String(statusFilter || "").trim() || "All";
  const selectedBatchMode = String(batchModeFilter || "").trim() || "All";
  const selectedBatchStudentsStatus = (() => {
    const t = String(batchStatusFilter || "").trim();
    if (t === "") return "Pursuing";
    if (t.toLowerCase() === "all") return "All";
    return t;
  })();
  const selectedBatchStudentsMode = String(batchModeFilter || "").trim() || "All";
  const selectedBatchStudentsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedClassRecordsTrainingProgram =
    String(trainingProgramFilter || "").trim() || "All";
  const selectedClassRecordsStatus = String(statusFilter || "").trim() || "All";
  const selectedClassesAttendanceStatus = (() => {
    const t = String(attendanceStatusFilter || "").trim();
    if (t === "Present" || t === "Absent") return t;
    return "";
  })();
  const handleBatchStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("status", value);
    else next.delete("status");
    setSearchParams(next);
  };
  const handleBatchModeFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("mode", value);
    else next.delete("mode");
    setSearchParams(next);
  };
  const handleBatchTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    setSearchParams(next);
  };
  const handleBatchStudentsStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value === "All") next.set("batch_status", "all");
    else if (value) next.set("batch_status", value);
    else next.delete("batch_status");
    setSearchParams(next);
  };
  const handleBatchStudentsModeFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("mode", value);
    else next.delete("mode");
    setSearchParams(next);
  };
  const handleBatchStudentsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    setSearchParams(next);
  };
  const handleClassRecordsTrainingProgramFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("training_program", value);
    else next.delete("training_program");
    setSearchParams(next);
  };
  const handleClassRecordsStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") next.set("status", value);
    else next.delete("status");
    setSearchParams(next);
  };
  const handleClassesAttendanceStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value === "Present" || value === "Absent") {
      next.set("attendance_status", value);
    } else {
      next.delete("attendance_status");
    }
    setSearchParams(next);
  };

  const selectedFeeInstallmentStatus = (() => {
    const t = String(statusFilter ?? "").trim();
    if (!t) return "All";
    const tl = t.toLowerCase();
    const allowed = ["pending", "paid", "overdue", "cancelled"];
    if (!allowed.includes(tl)) return "All";
    return tl.charAt(0).toUpperCase() + tl.slice(1);
  })();
  const selectedFeeInstallmentDuePeriod = (() => {
    const t = String(searchParams.get("due_period") ?? "").trim();
    return t || "All";
  })();
  const selectedStudentsAdmissionPeriod = (() => {
    const t = String(searchParams.get("admission_period") ?? "").trim();
    return t || "All";
  })();
  const rawStudentsTrainingProgramFilter = String(trainingProgramFilter || "").trim();
  const selectedStudentsTrainingProgram = (() => {
    if (!rawStudentsTrainingProgramFilter) return "All";
    const isNumeric =
      String(Number(rawStudentsTrainingProgramFilter)) === rawStudentsTrainingProgramFilter;
    if (isNumeric) return rawStudentsTrainingProgramFilter;
    const matched = (trainingProgramsAllOptions || []).find(
      (p) =>
        String(p?.name || "").trim().toLowerCase() ===
        rawStudentsTrainingProgramFilter.toLowerCase()
    );
    return matched ? String(matched.id) : rawStudentsTrainingProgramFilter;
  })();
  const hasAdhocStudentsTrainingProgramOption =
    selectedStudentsTrainingProgram !== "All" &&
    !(trainingProgramsAllOptions || []).some(
      (p) => String(p?.id ?? "").trim() === selectedStudentsTrainingProgram
    );

  const handleFeeInstallmentDuePeriodFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("due_period", value);
    } else {
      next.delete("due_period");
    }
    setSearchParams(next);
  };

  const handleFeeInstallmentStatusFilterChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("status", value.toLowerCase());
    } else {
      next.delete("status");
    }
    setSearchParams(next);
  };
  const handleStudentsAdmissionPeriodChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("admission_period", value);
    } else {
      next.delete("admission_period");
    }
    setSearchParams(next);
  };
  const handleStudentsTrainingProgramChange = (event) => {
    const value = String(event.target.value || "").trim();
    const next = new URLSearchParams(searchParams);
    if (value && value !== "All") {
      next.set("training_program", value);
    } else {
      next.delete("training_program");
    }
    setSearchParams(next);
  };

  const handleSnapshot = (row) => {
    const leadId = row?.organization_ems_lead_id ?? row?.id ?? row?.lead_id;
    if (
      leadId === undefined ||
      leadId === null ||
      String(leadId).trim() === "" ||
      String(leadId) === "-"
    ) {
      return;
    }

    const leadIds = (rowsForTable || [])
      .map((r) => r?.organization_ems_lead_id ?? r?.id ?? r?.lead_id)
      .filter(
        (id) =>
          id !== undefined &&
          id !== null &&
          String(id).trim() !== "" &&
          String(id) !== "-"
      )
      .map((id) => String(id));

    navigate(`/lead-activities/snapshot/${encodeURIComponent(String(leadId))}`, {
      state: { leadIds },
    });
  };

  const deleteBatchWithCascade = async (id, cascade = false) => {
    const response = await axios.delete(`${MAIN_URL}${apiEndpoint}/${id}`, {
      params: cascade ? { cascade: 1 } : {},
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response;
  };

  const showBatchRelatedDeleteToast = (id, data) => {
    const message =
      data?.message ||
      "This batch has related records. Remove them first or delete everything together.";
    const summary = Array.isArray(data?.related_summary)
      ? data.related_summary
      : [];

    toast.custom(
      (t) => (
        <Box
          sx={{
            maxWidth: 400,
            p: 2,
            bgcolor: "background.paper",
            boxShadow: 4,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "warning.main",
            opacity: t.visible ? 1 : 0,
            transition: "opacity 0.2s",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="warning.dark">
            Batch has related data
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
            {message}
          </Typography>
          {summary.length > 0 ? (
            <Box
              component="ul"
              sx={{ mt: 1, mb: 0.5, pl: 2.5, color: "text.secondary" }}
            >
              {summary.map((line) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={line}
                  sx={{ mb: 0.25 }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          ) : null}
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
            <Button size="small" onClick={() => toast.dismiss(t.id)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await deleteBatchWithCascade(id, true);
                  toast.success(
                    "Batch and all related records deleted successfully."
                  );
                  fetchData(false);
                } catch (err) {
                  toast.error(
                    err?.response?.data?.message ||
                      err?.response?.data?.error ||
                      "Failed to delete batch."
                  );
                }
              }}
            >
              Delete all
            </Button>
          </Box>
        </Box>
      ),
      { duration: Infinity, position: "top-center" }
    );
  };

  // ✅ Delete function (generic; batches support related-record guard + cascade)
  const resolveDeleteId = (idOrRow) => {
    if (idOrRow == null || idOrRow === "") return null;
    if (typeof idOrRow === "object" && !Array.isArray(idOrRow)) {
      return (
        idOrRow[tablePrimaryKey] ??
        idOrRow.organization_ems_batch_id ??
        idOrRow.id ??
        null
      );
    }
    return idOrRow;
  };

  const deleteFunction = async (idOrRow, options = {}) => {
    const id = resolveDeleteId(idOrRow);
    if (id == null || id === "" || Number.isNaN(Number(id))) {
      toast.error("Cannot delete: invalid record ID.");
      return;
    }
    const cascade = Boolean(options?.cascade);
    try {
      if (isBatchesList) {
        try {
          await deleteBatchWithCascade(id, cascade);
        } catch (error) {
          if (error?.response?.status === 409 && !cascade) {
            showBatchRelatedDeleteToast(id, error.response?.data || {});
            return;
          }
          throw error;
        }
      } else {
        await axios.delete(`${MAIN_URL}${apiEndpoint}/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      if (isBatchesList && cascade) {
        toast.success("Batch and all related records deleted successfully.");
      }
      fetchData(false);
    } catch (error) {
      console.error("Delete error:", error);
      const apiMessage =
        error?.response?.data?.message || error?.response?.data?.error;
      if (apiMessage) {
        toast.error(apiMessage);
      }
      throw error;
    }
  };

  return (
    <>
      <Layout4
        heading={title}
        Route={addRoute}
        btnName={isLeadsApiList ? "New Lead" : `Add ${title}`}
        subtitle={leadsListSubtitle}
        showAddButton={showAddButton}
      />
      <div className="ems-section-divider" />

      {!loadingConfig && (
      <TableDataGeneric
        key={
          isFeeInstallmentsList
            ? `fee-installments-${feeInstallmentFetchKey || "all"}`
            : undefined
        }
        tableName={tableName || title}
        primaryKey={tablePrimaryKey}
        heading={title}
        data={rowsForTable}
        loading={loading}
        configss={configColumns}
        loadedBackendConfig={tableConfig}
        sortname={tablePrimaryKey}
        Route={editRouteBase}
        mainKey={tablePrimaryKey}
        DeleteFunc={deleteFunction}
        EditFunc={(item) => navigate(`${editRouteBase}/${item.id}`)}
        ViewFunc={(item) =>
          navigate(
            `${editRouteBase.replace("/edit", "/view")}/${item.id}`
          )
        }
        token={localStorage.getItem("token")}
        showViewButton={effectiveShowViewButton}
        showEditButton={showEditButton}
        showDeleteButton={showDeleteButton}
        edit_delete_action={editDeleteActions}
        showReceiptButton={showReceiptButton}
        ReceiptFunc={handleReceipt}
        showEnrolledButton={showEnrolledButton}
        EnrolledFunc={handleEnrolled}
        showSnapshotButton={showSnapshotButton}
        SnapshotFunc={handleSnapshot}
        showLeadActivityButton={showLeadActivityButton}
        LeadActivityFunc={handleLeadActivity}
        showAdmissionButton={showAdmissionButton}
        AdmissionFunc={handleAdmission}
        showDemoParticipantButton={showDemoParticipantButton}
        DemoParticipantFunc={handleDemoParticipant}
        showFeePortalButton={showFeePortalButton}
        FeePortalFunc={handleFeePortal}
        showBatchAttendanceButton={showBatchAttendanceButton}
        BatchAttendanceFunc={handleBatchAttendanceMarking}
        showBatchClassesButton={showBatchClassesButton}
        BatchClassesFunc={handleBatchClassesFromBatch}
        showBatchStudentsButton={showBatchStudentsButton}
        BatchStudentsFunc={handleBatchStudentsFromBatch}
        showBatchRecordsButton={showBatchRecordsButton}
        BatchRecordsFunc={handleBatchRecordsFromBatchClass}
        serverSearchEnabled={isFeeInstallmentsList}
        serverSearchValue={feeInstallmentSearchInput}
        onServerSearchChange={setFeeInstallmentSearchInput}
        filterModule={useMetadataFilters ? filterModule || tableName : undefined}
        paginationData={useMetadataFilters ? paginationData : undefined}
        onSortChange={useMetadataFilters ? handleMetadataSortChange : undefined}
        onPageChange={useMetadataFilters ? handleMetadataPageChange : undefined}
        searchPlaceholder={
          isLeadsApiList
            ? "Search by name, phone or program..."
            : undefined
        }
        toolbarCenterContent={
          isBatchesApiList ? (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "nowrap",
                whiteSpace: "nowrap",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedBatchTrainingProgram}
                onChange={handleBatchTrainingProgramFilterChange}
                sx={{
                  minWidth: 240,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Mode"
                value={selectedBatchMode}
                onChange={handleBatchModeFilterChange}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedBatchStatus}
                onChange={handleBatchStatusFilterChange}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="Ongoing">Ongoing</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          ) : isBatchStudentsApiList ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: 760,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "minmax(260px, 1.5fr) minmax(170px, 1fr) minmax(170px, 1fr)",
                },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedBatchStudentsTrainingProgram}
                onChange={handleBatchStudentsTrainingProgramFilterChange}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedBatchStudentsStatus}
                onChange={handleBatchStudentsStatusFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pursuing">Pursuing</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Dropped">Dropped</MenuItem>
                <MenuItem value="On Hold">On Hold</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Mode"
                value={selectedBatchStudentsMode}
                onChange={handleBatchStudentsModeFilterChange}
                sx={{ minWidth: 0, width: "100%" }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
              </TextField>
            </Box>
          ) : isClassRecordsApiList ? (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedClassRecordsTrainingProgram}
                onChange={handleClassRecordsTrainingProgramFilterChange}
                sx={{
                  minWidth: 260,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedClassRecordsStatus}
                onChange={handleClassRecordsStatusFilterChange}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="Conducted">Conducted</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          ) : isClassesAttendanceApiList ? (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                select
                size="small"
                label="Attendance Status"
                value={selectedClassesAttendanceStatus}
                onChange={handleClassesAttendanceStatusFilterChange}
                displayEmpty
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                <MenuItem value="Present">Present</MenuItem>
                <MenuItem value="Absent">Absent</MenuItem>
              </TextField>
            </Box>
          ) : isFeeInstallmentsList ? (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                select
                size="small"
                label="Due Period"
                value={selectedFeeInstallmentDuePeriod}
                onChange={handleFeeInstallmentDuePeriodFilterChange}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="All">All</MenuItem>
                {(config?.duePeriodOptions || []).map((option) => (
                  <MenuItem key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={selectedFeeInstallmentStatus}
                onChange={handleFeeInstallmentStatusFilterChange}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Overdue">Overdue</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          ) : isStudentsList || isAdmissionsApiList ? (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                select
                size="small"
                label="Training Program"
                value={selectedStudentsTrainingProgram}
                onChange={handleStudentsTrainingProgramChange}
                sx={{
                  minWidth: 260,
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {trainingProgramsAllOptions.map((program) => (
                  <MenuItem key={String(program.id)} value={String(program.id)}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Admission Period"
                value={selectedStudentsAdmissionPeriod}
                onChange={handleStudentsAdmissionPeriodChange}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="All">All</MenuItem>
                {(config?.admissionPeriodOptions || []).map((option) => (
                  <MenuItem key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ) : null
        }
        config={resolvedTableConfig}
      />
      )}

      <AdmissionFeePortalDialog
        open={feePortalOpen}
        loading={feePortalLoading}
        admission={feePortalAdmission}
        organizationId={organizationId ?? org?.organization_id}
        onClose={() => {
          setFeePortalOpen(false);
          setFeePortalAdmission(null);
        }}
      />
    </>
  );
}

export default CommonList;
